import React, { useState, useMemo, useEffect } from 'react';
import {
    Box,
    Stack,
    Typography,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    InputAdornment,
    Chip,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    Fade,
    Collapse,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import {
    Search as SearchIcon,
    Refresh as RefreshIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    KeyboardArrowUp as KeyboardArrowUpIcon,

    Download as DownloadIcon,
    Upload as UploadIcon,
    Edit as EditIcon,
    BeachAccess as LeaveIcon,
    Groups as GroupsIcon,
    HourglassEmpty as PendingIcon,
    CheckCircle as ApprovedIcon,
} from '@mui/icons-material';
import { useLeaveSummaryReport, useBulkUpdateLeaves } from '../../hooks/useLeaves';
import { useUpdateEmployeeLeaves } from '../../hooks/useEmployees';
import { exportLeaveSummary, downloadLeaveTemplate } from '../../api';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    getLeaveCaps,
    isProbation,
    validateLeaveField,
    validateLeaveForm,
} from '../../utils/leaveCaps';
import { TABLE_PAPER_SX, HEAD_CELL_SX, BODY_CELL_SX, TABLE_ROW_SX } from '../../components/common/tableStyles';
import { formatDate } from '../../utils/dateFormat';
import { SELECTED_MENU_PROPS } from '../../components/StyledComponents/StyledComponent';

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

// ---- FORTHEYE soft-card tokens (shared with the employee portal) ----
const CARD_SX = {
    p: { xs: 2, sm: 2.5, md: 3 },
    borderRadius: '22px',
    bgcolor: '#fff',
    border: '1px solid #eef0f4',
    boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';
const OUTLINED_BTN = { textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280', borderColor: '#e5e7eb' };
const GRADIENT_BTN = {
    textTransform: 'none', fontWeight: 700, borderRadius: '12px',
    background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
    '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
};

// Soft tinted status chip sx (matches the rest of the portal).
const chipSx = (fg, bg) => ({ fontWeight: 700, bgcolor: bg, color: fg, border: 'none' });
const statusChipSx = (status) => {
    const s = String(status).toUpperCase();
    if (s === 'ACCEPTED' || s === 'CONFIRMED') return chipSx('#16a34a', '#f0fdf4');
    if (s === 'PENDING' || s === 'PROBATION') return chipSx('#f59e0b', '#fffbeb');
    if (s === 'DECLINED' || s === 'REJECTED') return chipSx('#ef4444', '#fef2f2');
    return chipSx('#6b7280', '#f3f4f6');
};

// Clickable stat tile used as a quick filter.
const StatTile = ({ icon, label, value, color, active, onClick }) => (
    <Paper
        elevation={0}
        onClick={onClick}
        sx={{
            ...CARD_SX,
            p: { xs: 1.75, md: 2 },
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            border: active ? `1.5px solid ${color}` : '1px solid #eef0f4',
            bgcolor: active ? `${color}0d` : '#fff',
            '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 28px rgba(17,24,39,0.08)' },
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: '11px', bgcolor: `${color}14`, color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {React.cloneElement(icon, { sx: { fontSize: 20 } })}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: '#6b7280', fontWeight: 600, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Typography>
                <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: active ? color : '#111827', lineHeight: 1.1 }}>{value}</Typography>
            </Box>
        </Box>
    </Paper>
);

function useDebouncedValue(value, delay) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
}

const LeaveTracker = () => {
    const { data: reportData, isLoading, error, refetch } = useLeaveSummaryReport();
    const { mutate: updateLeaves, isPending: isUpdating } = useUpdateEmployeeLeaves();
    const { mutate: bulkUpdate, isPending: isBulkUpdating } = useBulkUpdateLeaves();
    const [searchTerm, setSearchTerm] = useState('');
    const [deptFilter, setDeptFilter] = useState('All');
    const [statFilter, setStatFilter] = useState('ALL'); // 'ALL', 'PENDING', 'APPROVED'
    const [expandedRow, setExpandedRow] = useState(null);
    const fileInputRef = React.useRef(null);

    // Debounced search + pagination so the page renders ~25 rows at a time
    // instead of all ~221 heavy rows at once (which caused the hang).
    const debouncedSearch = useDebouncedValue(searchTerm, 250);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    // Arrived from the attendance "Leaves taken" tile → focus that employee.
    const location = useLocation();
    useEffect(() => {
        const empNo = location.state?.employeeNo;
        if (empNo) setSearchTerm(String(empNo));
    }, [location.state]);

    // Update Leave Dialog state
    const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [leaveBalances, setLeaveBalances] = useState({
        total_leaves: '',
        sick_leaves: '',
        casual_leaves: '',
    });
    const [leaveErrors, setLeaveErrors] = useState({});

    const departments = useMemo(() => {
        if (!reportData) return ['All'];
        const depts = new Set(reportData.map(emp => emp.department).filter(Boolean));
        return ['All', ...Array.from(depts)];
    }, [reportData]);

    const filteredData = useMemo(() => {
        if (!reportData) return [];
        return reportData.filter(emp => {
            const matchesSearch = emp.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
                emp.employee_no.toLowerCase().includes(debouncedSearch.toLowerCase());
            const matchesDept = deptFilter === 'All' || emp.department === deptFilter;

            // Card based filtering
            let matchesStat = true;
            if (statFilter === 'PENDING') {
                matchesStat = emp.stats.pending > 0;
            } else if (statFilter === 'APPROVED') {
                matchesStat = emp.stats.approved > 0;
            }

            return matchesSearch && matchesDept && matchesStat;
        });
    }, [reportData, debouncedSearch, deptFilter, statFilter]);

    // Only the current page is rendered — this removes the load hang.
    const paginatedData = useMemo(
        () => filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [filteredData, page, rowsPerPage]
    );

    // Snap back to page 1 whenever the filtered result set changes.
    useEffect(() => { setPage(0); }, [debouncedSearch, deptFilter, statFilter, rowsPerPage]);

    const stats = useMemo(() => {
        if (!reportData) return { total: 0, pending: 0, approvedToday: 0 };
        return {
            total: reportData.length,
            pending: reportData.reduce((acc, emp) => acc + emp.stats.pending, 0),
            leavesInPeriod: reportData.reduce((acc, emp) => acc + emp.stats.approved, 0),
        };
    }, [reportData]);

    // Open leave update dialog for a specific employee
    const handleOpenLeaveDialog = (emp) => {
        setSelectedEmployee(emp);
        setLeaveBalances({
            total_leaves: emp.total_leaves ?? '',
            sick_leaves: emp.sick_leaves ?? '',
            casual_leaves: emp.casual_leaves ?? '',
        });
        setLeaveErrors({});
        setLeaveDialogOpen(true);
    };

    const handleCloseLeaveDialog = () => {
        setLeaveDialogOpen(false);
        setSelectedEmployee(null);
        setLeaveBalances({ total_leaves: '', sick_leaves: '', casual_leaves: '' });
        setLeaveErrors({});
    };

    const handleLeaveChange = (event) => {
        const { name, value } = event.target;
        const status = selectedEmployee?.employment_status;
        const error = validateLeaveField(name, value, status);

        // Toast only on the valid -> invalid transition so feedback fires once
        // per crossing rather than on every keystroke.
        if (error && !leaveErrors[name]) {
            toast.error(error, { autoClose: 4000, position: 'top-right' });
        }

        setLeaveBalances(prev => ({ ...prev, [name]: value }));
        setLeaveErrors(prev => ({ ...prev, [name]: error }));
    };

    const handleLeaveSubmit = () => {
        if (!selectedEmployee) return;

        const errors = validateLeaveForm({
            employmentStatus: selectedEmployee.employment_status,
            ...leaveBalances,
        });
        setLeaveErrors(errors);
        if (Object.keys(errors).length > 0) {
            toast.error('Please fix the highlighted fields before saving.', {
                autoClose: 4000, position: 'top-right',
            });
            return;
        }

        updateLeaves({
            employeeNo: selectedEmployee.employee_no,
            leaveData: {
                total_leaves: leaveBalances.total_leaves !== '' ? parseFloat(leaveBalances.total_leaves) : undefined,
                sick_leaves: leaveBalances.sick_leaves !== '' ? parseFloat(leaveBalances.sick_leaves) : undefined,
                casual_leaves: leaveBalances.casual_leaves !== '' ? parseFloat(leaveBalances.casual_leaves) : undefined,
            }
        }, {
            onSuccess: () => {
                toast.success('Leave balance updated successfully.', {
                    autoClose: 4000, position: 'top-right',
                });
                handleCloseLeaveDialog();
                refetch();   // immediately pull the new balances into the table
            },
            onError: (error) => {
                const backendMsg = error?.response?.data?.error
                    || error?.response?.data?.detail
                    || 'Failed to update leave balance. Please try again.';
                toast.error(backendMsg, { autoClose: 4000, position: 'top-right' });
            },
        });
    };

    const handleExport = async () => {
        try {
            toast.info('Generating Excel report...');
            await exportLeaveSummary();
            toast.success('Report downloaded successfully');
        } catch (err) {
            toast.error('Export failed');
            console.error('Export failed:', err);
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            await downloadLeaveTemplate();
            toast.success('Template downloaded');
        } catch (error) {
            toast.error('Failed to download template');
        }
    };

    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            bulkUpdate(file);
            event.target.value = ''; // Reset input
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ pb: 2 }}>
                <Alert severity="error" sx={{ borderRadius: '14px' }}>Error loading leave report: {error.message}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
            {/* Header card */}
            <Fade in timeout={600}>
                <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                            <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <LeaveIcon />
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                                    Leave Tracker Dashboard
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                    Track balances and recent leave requests across the team
                                </Typography>
                            </Box>
                        </Box>
                        <Button startIcon={<RefreshIcon />} variant="outlined" onClick={() => refetch()} sx={OUTLINED_BTN}>
                            Refresh Data
                        </Button>
                    </Stack>
                </Paper>
            </Fade>

            {/* Stats Section */}
            <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 2, md: 3 } }}>
                <Grid item xs={12} sm={4}>
                    <StatTile
                        icon={<GroupsIcon />} color={ACCENT} label="Total Employees" value={stats.total}
                        active={statFilter === 'ALL'} onClick={() => setStatFilter('ALL')}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StatTile
                        icon={<PendingIcon />} color="#f59e0b" label="Pending Requests" value={stats.pending}
                        active={statFilter === 'PENDING'} onClick={() => setStatFilter('PENDING')}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StatTile
                        icon={<ApprovedIcon />} color="#16a34a" label="Approved (Total)" value={stats.leavesInPeriod}
                        active={statFilter === 'APPROVED'} onClick={() => setStatFilter('APPROVED')}
                    />
                </Grid>
            </Grid>

            {/* Filters Section */}
            <Paper elevation={0} sx={{ ...CARD_SX, p: { xs: 1.75, sm: 2 }, mb: { xs: 2, md: 3 }, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    placeholder="Search by name or ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ minWidth: 300 }}
                />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Department</InputLabel>
                    <Select
                        value={deptFilter}
                        label="Department"
                        onChange={(e) => setDeptFilter(e.target.value)}
                        MenuProps={SELECTED_MENU_PROPS}
                    >
                        {departments.map(dept => (
                            <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box flexGrow={1} />
                <input
                    type="file"
                    ref={fileInputRef}
                    hidden
                    accept=".xlsx, .xls"
                    onChange={handleFileChange}
                />
                <Button
                    startIcon={<DownloadIcon />}
                    variant="outlined"
                    onClick={handleDownloadTemplate}
                    sx={{ ...OUTLINED_BTN, mr: 1 }}
                >
                    Download Template
                </Button>
                <Button
                    startIcon={<UploadIcon />}
                    variant="outlined"
                    onClick={handleImportClick}
                    disabled={isBulkUpdating}
                    sx={{ ...OUTLINED_BTN, mr: 1 }}
                >
                    {isBulkUpdating ? 'Importing...' : 'Import Leaves'}
                </Button>
                <Button
                    startIcon={<DownloadIcon />}
                    variant="contained"
                    disableElevation
                    onClick={handleExport}
                    sx={GRADIENT_BTN}
                >
                    Export Report
                </Button>
            </Paper>

            {/* Main Table */}
            <TableContainer component={Paper} sx={{ ...TABLE_PAPER_SX, borderRadius: '22px', border: '1px solid #eef0f4', boxShadow: '0 6px 24px rgba(17,24,39,0.05)' }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={HEAD_CELL_SX} />
                            <TableCell sx={HEAD_CELL_SX}>Employee</TableCell>
                            <TableCell sx={HEAD_CELL_SX}>Department</TableCell>
                            <TableCell sx={HEAD_CELL_SX}>Status</TableCell>
                            <TableCell sx={HEAD_CELL_SX} align="center">Total Bal</TableCell>
                            <TableCell sx={HEAD_CELL_SX} align="center">Sick</TableCell>
                            <TableCell sx={HEAD_CELL_SX} align="center">Casual</TableCell>
                            <TableCell sx={HEAD_CELL_SX} align="center">Stats</TableCell>
                            <TableCell sx={HEAD_CELL_SX} align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedData.map((emp) => (
                            <React.Fragment key={emp.employee_no}>
                                <TableRow sx={{ ...TABLE_ROW_SX, '& > *': { borderBottom: 'unset' } }}>
                                    <TableCell sx={BODY_CELL_SX}>
                                        <IconButton size="small" onClick={() => setExpandedRow(expandedRow === emp.employee_no ? null : emp.employee_no)}>
                                            {expandedRow === emp.employee_no ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                        </IconButton>
                                    </TableCell>
                                    <TableCell sx={BODY_CELL_SX}>
                                        <Box>
                                            <Typography variant="body1" fontWeight={600}>{emp.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">{emp.employee_no}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={BODY_CELL_SX}>{emp.department}</TableCell>
                                    <TableCell sx={BODY_CELL_SX}>
                                        <Chip
                                            label={emp.employment_status || 'N/A'}
                                            size="small"
                                            sx={statusChipSx(emp.employment_status)}
                                        />
                                    </TableCell>
                                    <TableCell sx={BODY_CELL_SX} align="center">
                                        <Typography fontWeight={700}>{emp.total_leaves}</Typography>
                                    </TableCell>
                                    <TableCell sx={BODY_CELL_SX} align="center">{emp.sick_leaves}</TableCell>
                                    <TableCell sx={BODY_CELL_SX} align="center">{emp.casual_leaves}</TableCell>
                                    <TableCell sx={BODY_CELL_SX} align="center">
                                        <Tooltip title={`Approved: ${emp.stats.approved}, Pending: ${emp.stats.pending}`}>
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                                                <Typography variant="caption">{emp.stats.approved}</Typography>
                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main', ml: 1 }} />
                                                <Typography variant="caption">{emp.stats.pending}</Typography>
                                            </Box>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell sx={BODY_CELL_SX} align="center">
                                        <Tooltip title="Update Leave Balance">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenLeaveDialog(emp)}
                                                sx={{
                                                    color: ACCENT,
                                                    bgcolor: `${ACCENT}14`,
                                                    '&:hover': { bgcolor: `${ACCENT}22` },
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                                        <Collapse in={expandedRow === emp.employee_no} timeout="auto" unmountOnExit>
                                            <Box sx={{ margin: 2, p: 2, bgcolor: '#fcfdff', border: '1px solid #eef0f4', borderRadius: '14px' }}>
                                                <Typography variant="h6" gutterBottom component="div" sx={{ fontSize: '1rem', fontWeight: 700 }}>
                                                    Recent Leave Requests
                                                </Typography>
                                                {emp.recent_requests && emp.recent_requests.length > 0 ? (
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell sx={HEAD_CELL_SX}>Requested On</TableCell>
                                                                <TableCell sx={HEAD_CELL_SX}>Period</TableCell>
                                                                <TableCell sx={HEAD_CELL_SX}>Type</TableCell>
                                                                <TableCell sx={HEAD_CELL_SX}>Status</TableCell>
                                                                <TableCell sx={HEAD_CELL_SX}>Duration</TableCell>
                                                                <TableCell sx={HEAD_CELL_SX}>Reason</TableCell>
                                                                <TableCell sx={HEAD_CELL_SX}>Action Info</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {emp.recent_requests.map((request) => (
                                                                <TableRow key={request.id} sx={TABLE_ROW_SX}>
                                                                    <TableCell sx={BODY_CELL_SX}>
                                                                        {formatDate(request.created_at)}
                                                                    </TableCell>
                                                                    <TableCell sx={BODY_CELL_SX}>
                                                                        {formatDate(request.start_date)} to {formatDate(request.end_date)}
                                                                    </TableCell>
                                                                    <TableCell sx={BODY_CELL_SX}>{request.leave_type}</TableCell>
                                                                    <TableCell sx={BODY_CELL_SX}>
                                                                        <Chip
                                                                            label={request.status}
                                                                            size="small"
                                                                            sx={{ height: 20, fontSize: '0.65rem', ...statusChipSx(request.status) }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell sx={BODY_CELL_SX}>{request.leave_days} Days</TableCell>
                                                                    <TableCell sx={{ ...BODY_CELL_SX, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={request.reason}>
                                                                        {request.reason}
                                                                    </TableCell>
                                                                    <TableCell sx={BODY_CELL_SX}>
                                                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                                            {request.status === 'ACCEPTED' && request.approved_date && (
                                                                                <Typography variant="caption" display="block" sx={{ whiteSpace: 'nowrap' }}>Approved: {formatDate(request.approved_date)}</Typography>
                                                                            )}
                                                                            {request.status === 'DECLINED' && request.declined_date && (
                                                                                <Typography variant="caption" display="block" sx={{ whiteSpace: 'nowrap' }}>Declined: {formatDate(request.declined_date)}</Typography>
                                                                            )}
                                                                            {request.status_reason && (
                                                                                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal', maxWidth: 150 }}>{request.status_reason}</Typography>
                                                                            )}
                                                                            {!request.approved_date && !request.declined_date && !request.status_reason && '-'}
                                                                        </Box>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">No recent leave history.</Typography>
                                                )}
                                            </Box>
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                            </React.Fragment>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={filteredData.length}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                    rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                />
            </TableContainer>

            {/* Update Leave Balance Dialog */}
            <Dialog
                open={leaveDialogOpen}
                onClose={handleCloseLeaveDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: '22px', p: 1 }
                }}
            >
                <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
                    Update Leave Balance
                    {selectedEmployee && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {selectedEmployee.name} ({selectedEmployee.employee_no})
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    {(() => {
                        const status = selectedEmployee?.employment_status;
                        const caps = getLeaveCaps(status);
                        const probation = isProbation(status);
                        const totalMax = caps?.total ?? 20;
                        const sickMax = caps?.sick ?? 10;
                        const casualMax = caps?.casual ?? 10;
                        return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                                <TextField
                                    label="Total Leaves"
                                    name="total_leaves"
                                    type="number"
                                    value={leaveBalances.total_leaves}
                                    onChange={handleLeaveChange}
                                    fullWidth
                                    variant="outlined"
                                    error={Boolean(leaveErrors.total_leaves)}
                                    helperText={leaveErrors.total_leaves || `Maximum ${totalMax} days`}
                                    InputProps={{ inputProps: { min: 0, max: totalMax } }}
                                />
                                <TextField
                                    label="Sick Leaves"
                                    name="sick_leaves"
                                    type="number"
                                    value={leaveBalances.sick_leaves}
                                    onChange={handleLeaveChange}
                                    fullWidth
                                    variant="outlined"
                                    disabled={probation}
                                    error={Boolean(leaveErrors.sick_leaves)}
                                    helperText={
                                        leaveErrors.sick_leaves
                                            || (probation ? 'Not applicable during probation' : `Maximum ${sickMax} days`)
                                    }
                                    InputProps={{ inputProps: { min: 0, max: sickMax } }}
                                />
                                <TextField
                                    label="Casual Leaves"
                                    name="casual_leaves"
                                    type="number"
                                    value={leaveBalances.casual_leaves}
                                    onChange={handleLeaveChange}
                                    fullWidth
                                    variant="outlined"
                                    disabled={probation}
                                    error={Boolean(leaveErrors.casual_leaves)}
                                    helperText={
                                        leaveErrors.casual_leaves
                                            || (probation ? 'Not applicable during probation' : `Maximum ${casualMax} days`)
                                    }
                                    InputProps={{ inputProps: { min: 0, max: casualMax } }}
                                />
                            </Box>
                        );
                    })()}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleCloseLeaveDialog} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280' }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleLeaveSubmit}
                        variant="contained"
                        disableElevation
                        disabled={isUpdating || Object.values(leaveErrors).some(Boolean)}
                        sx={{ ...GRADIENT_BTN, px: 4 }}
                    >
                        {isUpdating ? <CircularProgress size={20} color="inherit" /> : 'Update Leaves'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LeaveTracker;
